import React, { useEffect, useMemo, useState } from 'react';
import { Flag, MoreHorizontal, Pencil, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFormik, FormikProvider } from 'formik';
import toast from 'react-hot-toast';
import { useCrmNavigation } from 'hooks/useCrmNavigation';
import UserAvatar from 'components/common/UserAvatar';
import ActionsDropdown from 'components/common/ActionsDropdown';
import FormInput from 'components/common/FormInput';
import { CustomSelect } from 'components/common/CustomSelect';
import CustomCalendar from 'components/common/CustomCalendar';
import { useAppSelector } from 'store';
import { selectAuthUser } from 'store/slices/auth/auth.selector';
import {
  useContactOwners,
  useCrmValueOptions,
  useUpdateDealMutation,
  useDeleteDealMutation,
} from 'service/crm';
import { ContactOwner } from 'types/crm/contacts';
import { UserIcon, FlagIcon } from 'assets/Icons';
import {
  DealDetail,
  DealFormValues,
  PRIORITY_PILL,
  DEAL_PRIORITY_LABELS,
  DEAL_TYPE_LABELS,
  DEFAULT_PIPELINE,
  PIPELINE_OPTIONS,
  CURRENCY_OPTIONS,
  DEAL_TYPE_OPTIONS,
  PRIORITY_OPTIONS,
} from 'types/crm/deal';
import { dealsValidationSchema } from 'utils/validation';
import { formatDate, formatDateKey, formatDealUsdAmount } from 'utils/helpers';
import InlineSelectField from '../Dropdowns/InlineSelectFields';
import InlineOwnerField from '../Dropdowns/InlineOwnerFiled';
import { useRecordAccess } from 'context/crm/RecordAccessContext';
import FormTextarea from 'components/common/FormTextArea';
import DeleteModal, { CascadeItem } from 'components/Modals/CrmDeleteModal';



interface DealDetailsCardProps {
  deal: DealDetail;
}

const buildInitialValues = (deal: DealDetail): DealFormValues => ({
  name: deal.name ?? '',
  pipeline: deal.pipeline ?? DEFAULT_PIPELINE,
  dealStage: deal.dealStage ?? '',
  currency: deal.currency ?? 'INR',
  amount: deal.amount != null ? String(deal.amount) : '',
  closeDate: deal.closeDate ? deal.closeDate.slice(0, 10) : '',
  owner: deal.owner?.uuid ?? '',
  dealType: deal.dealType ?? '',
  priority: deal.priority ?? '',
  description: deal.description ?? '',
});

const label = 'text-[11px] font-medium tracking-wide uppercase text-neutral-medium';

const DealDetailsCard: React.FC<DealDetailsCardProps> = ({ deal }) => {
  const authUser = useAppSelector(selectAuthUser);

  /**
   * RBAC (section 19): a CRM User edits this deal only when they own it, or have
   * an assigned Task or Note on it — encoded in `deal.canEdit`. Admin and Manager
   * are unrestricted. Ownership reassignment stays Admin/Manager only
   * (sections 2, 3). Resolved by <RecordAccessProvider> on the page.
   */
  const { canEdit, canDelete, canReassignOwner } = useRecordAccess();

  const { data: owners = [], isLoading: ownersLoading } = useContactOwners(
    authUser?.orgUuid as string,
  );
  const updateDeal = useUpdateDealMutation();

  // The organisation's configured stages (Settings → CRM Values), Active only,
  // from GET /crm/values/deal-stage/assignable. The deal's own stage is passed
  // in so it stays selectable even after an admin deactivates it.
  const { options: dealStageOptions, isLoading: isDealStageLoading } =
    useCrmValueOptions('DEAL_STAGE', deal.dealStage, deal.dealStageLabel);
  const deleteDeal = useDeleteDealMutation();
  const navigate = useNavigate();
  const { buildDealsUrl } = useCrmNavigation();
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const ownerOptions = useMemo(
    () => owners.map((o: ContactOwner) => ({ value: o.uuid, label: `${o.name}`.trim() })),
    [owners],
  );

  const formik = useFormik<DealFormValues>({
    initialValues: buildInitialValues(deal),
    validationSchema: dealsValidationSchema,
    // Only re-sync from the server while the user isn't mid-edit, so a
    // background refetch can't wipe out what they've typed.
    enableReinitialize: !isEditing,
    onSubmit: async (values) => {
      if (!canEdit) {
        toast.error('You do not have permission to update this deal.');
        setIsEditing(false);
        return;
      }
      try {
        await updateDeal.mutateAsync({
          dealUuid: deal.uuid,
          dealName: values.name.trim(),
          pipeline: values.pipeline || undefined,
          dealStage: values.dealStage || undefined,
          amount: values.amount !== '' ? Number(values.amount) : undefined,
          currency: values.currency || undefined,
          closeDate: values.closeDate || undefined,
          // Ownership is Admin/Manager only — omit entirely otherwise, so a CRM
          // User's submit can never carry an owner change.
          ...(canReassignOwner ? { dealOwnerUuid: values.owner || undefined } : {}),
          dealType: values.dealType || undefined,
          priority: values.priority || undefined,
          description: values.description || undefined,
        });
        toast.success('Deal updated successfully');
        setIsEditing(false);
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ??
          err?.message ??
          'Failed to update deal. Please try again.',
        );
      }
    },
  });

  const { values, touched, errors, handleBlur, setFieldValue, getFieldProps } = formik;

  // Section 23: access can disappear mid-session (Task/Note reassigned, Owner
  // changed). Collapse any open edit surface rather than leaving a dead form.
  useEffect(() => {
    if (!canEdit) {
      setIsEditing(false);
      setIsDeleteOpen(false);
    }
  }, [canEdit]);

  const fieldError = (field: keyof DealFormValues) =>
    touched[field] && errors[field] ? (errors[field] as string) : undefined;

  const isSaving = formik.isSubmitting || updateDeal.isPending;
  const priority = deal.priority;

  /** PATCH a single field from the read view. Returns the promise so the
   *  inline field can roll back if the request fails. */
  const patchDeal = (patch: Record<string, unknown>) => {
    if (!canEdit) {
      const err = new Error('You do not have permission to update this deal.');
      toast.error(err.message);
      return Promise.reject(err);
    }
    return updateDeal.mutateAsync({ dealUuid: deal.uuid, ...patch } as any);
  };

  const handleEdit = () => {
    if (!canEdit) return;
    formik.resetForm({ values: buildInitialValues(deal) });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    formik.resetForm({ values: buildInitialValues(deal) });
    setIsEditing(false);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete) {
      toast.error('You do not have permission to delete this deal.');
      setIsDeleteOpen(false);
      return;
    }
    try {
      await deleteDeal.mutateAsync({ dealUuid: deal.uuid });
      toast.success('Deal deleted successfully');
      setIsDeleteOpen(false);
      // The deal this page is built on is gone, so head back to the list.
      // `replace` keeps the dead URL out of history.
      navigate(buildDealsUrl(), { replace: true });
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ??
        err?.message ??
        'Failed to delete deal. Please try again.',
      );
    }
  };

  /** Records destroyed alongside the deal. Zero-count rows are omitted. */
  const deleteCascade = useMemo<CascadeItem[]>(() => {
    const { notesCount, tasksCount, activitiesCount } = deal;

    return [
      { count: 1, label: `Deal — ${deal.name}` },
      ...(notesCount ? [{ count: notesCount, label: 'Note' }] : []),
      ...(tasksCount ? [{ count: tasksCount, label: 'Task' }] : []),
      ...(activitiesCount
        ? [{ count: activitiesCount, label: 'Logged activity', labelPlural: 'Logged activities' }]
        : []),
    ];
  }, [deal]);

  /**
   * Section 13: the three-dot menu holds only modification actions, so a
   * view-only user should not see the menu at all.
   */
  const rowActions = useMemo(() => {
    const actions: Array<{
      label: string;
      icon: React.ReactNode;
      variant?: 'danger';
      onClick: () => void;
    }> = [];

    if (canEdit) {
      actions.push({
        label: 'Edit Deal',
        icon: <Pencil className="w-4 h-4" />,
        onClick: handleEdit,
      });
    }
    if (canDelete) {
      actions.push({
        label: 'Delete Deal',
        icon: <Trash2 className="w-4 h-4" />,
        variant: 'danger',
        onClick: () => setIsDeleteOpen(true),
      });
    }
    return actions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, canDelete, deal.uuid]);

  return (
    <FormikProvider value={formik}>
      <div className="w-full h-full rounded-l-xl bg-primary border-r border-primary-subtle p-5 space-y-6 overflow-y-auto">
        {/* Header */}
        <div className="border-b border-primary-subtle pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <UserAvatar name={deal.name ?? ''} className="w-12 h-12 text-base" />
              {!isEditing && (
                <h3 className="text-base font-semibold text-neutral leading-snug break-words capitalize">
                  {deal.name}
                </h3>
              )}
            </div>
            {isEditing && canEdit ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => formik.handleSubmit()}
                  disabled={isSaving || !formik.dirty || !formik.isValid}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-blue-500 disabled:opacity-60"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="p-1.5 rounded-lg text-neutral-medium hover:text-neutral hover:bg-primary-subtle/50"
                  aria-label="Cancel edit"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              rowActions.length > 0 && (
                <ActionsDropdown
                  triggerIcon={<MoreHorizontal className="w-5 h-5 text-neutral-medium" />}
                  actions={rowActions}
                />
              )
            )}
          </div>
        </div>

        {isEditing && canEdit ? (
          <div className="space-y-5">
            <FormInput
              {...getFieldProps('name')}
              label="Deal Name"
              required
              placeholder="Enter deal name"
              error={fieldError('name')}
              className={{ input: 'text-sm px-3 h-[40px] placeholder:text-sm', label: 'text-xs ' }}
            />

            <div>
              <label className={'block text-xs font-normal text-[#9CA3AF] mb-[11px] leading-[100%]'}>
                Amount
              </label>
              <div className="flex items-center gap-2">
                <CustomSelect
                  name="currency"
                  options={CURRENCY_OPTIONS}
                  className={{ root: 'w-[90px] shrink-0', input: 'px-3 h-[40px]', value: 'text-sm' }}
                />
                <div className="flex-1">
                  <FormInput
                    {...getFieldProps('amount')}
                    name="amount"
                    type="number"
                    placeholder="Enter amount"
                    error={fieldError('amount')}
                    className={{
                      input: 'text-sm px-3 h-[40px] placeholder:text-sm',
                      label: 'text-xs ',
                    }}
                  />
                </div>
              </div>
            </div>

            <CustomCalendar
              label="Close Date"
              placeholder="Select close date"
              value={values.closeDate}
              minDate={new Date()}
              onChange={(date) => setFieldValue('closeDate', formatDateKey(date))}
              className="!h-[40px] px-3"
              labelClassName="!text-xs"
              textClassName="!text-sm"
            />

            <CustomSelect
              label="Deal Stage"
              name="dealStage"
              options={dealStageOptions}
              placeholder={isDealStageLoading ? 'Loading stages...' : 'Select deal stage'}
              disabled={isDealStageLoading}
              required
              className={{ input: 'px-3 h-[40px]', value: 'text-sm', label: 'text-xs ' }}
            />

            <CustomSelect
              label="Pipeline"
              name="pipeline"
              options={PIPELINE_OPTIONS}
              placeholder="Select pipeline"
              required
              className={{ input: 'px-3 h-[40px]', value: 'text-sm', label: 'text-xs ' }}
            />

            <CustomSelect
              label="Deal Type"
              name="dealType"
              options={DEAL_TYPE_OPTIONS}
              placeholder="Select deal type"
              className={{ input: 'px-3 h-[40px]', value: 'text-sm', label: 'text-xs ' }}
            />

            <CustomSelect
              label="Priority"
              name="priority"
              options={PRIORITY_OPTIONS}
              placeholder="Select priority"
              icon={<FlagIcon />}
              className={{ input: 'px-3 h-[40px]', value: 'text-sm', label: 'text-xs ' }}
            />

            {/* Owner — Admin/Manager may reassign; everyone else read-only */}
            {canReassignOwner ? (
              <CustomSelect
                label="Deal Owner"
                name="owner"
                options={ownerOptions}
                placeholder={ownersLoading ? 'Loading owners...' : 'Select deal owner'}
                icon={<UserIcon size={18} />}
                className={{ input: 'px-3 h-[40px]', value: 'text-sm', label: 'text-xs ' }}
              />
            ) : (
              <div className="rounded-lg border border-primary-subtle bg-primary-surface px-3 py-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-medium">
                  Deal Owner
                </div>
                <div className="mt-0.5 text-sm text-neutral">
                  {deal.owner?.name || 'Unassigned'}
                </div>
              </div>
            )}
            <FormTextarea
              name="description"
              label="Insights"
              placeholder="Add insights about this contact"
              maxLength={500}
              // darkBg
              className={{ label: 'text-sm', textarea: 'text-sm' }}
            />
          </div>
        ) : (
          <>
            {/* Deal value */}
            <div className="rounded-xl bg-primary-subtle/30 bg-primary-light border border-primary-subtle p-4">
              <div className="text-[13px] text-neutral-faded">Deal Value</div>
              <div className="text-[26px] font-bold text-green-400 mt-1 leading-tight">
                {formatDealUsdAmount(deal)}
              </div>
              <div className="text-[13px] text-neutral-faded mt-1">
                Expected close · {formatDate(deal.closeDate, '—')}
              </div>
            </div>

            {/* Deal stage — inline PATCH with edit access, static without */}
            {canEdit ? (
              <InlineSelectField<string>
                label="Deal Stage"
                options={dealStageOptions}
                value={deal.dealStage ?? undefined}
                placeholder="Select deal stage"
                disabled={isDealStageLoading}
                onSave={(next: any) => patchDeal({ dealStage: next })}
                errorMessage="Could not update the deal stage. Please try again."
              />
            ) : (
              <div className="rounded-lg border border-primary-subtle bg-primary-surface px-3 py-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-medium">
                  Deal Stage
                </div>
                <div className="mt-0.5 text-sm text-neutral">
                  {deal.dealStageLabel || '—'}
                </div>
              </div>
            )}

            {/* Pipeline / Deal type / Priority */}
            <div className="border rounded-xl bg-primary-light border-primary-subtle">
              <div className="px-4 py-4 border-b border-primary-subtle">
                <div className={label}>Pipeline</div>
                <div className="text-sm text-emphasis mt-1">
                  {deal.pipelineLabel ? `${deal.pipelineLabel} Pipeline` : '—'}
                </div>
              </div>
              <div className="px-4 py-4 border-b border-primary-subtle">
                <div className={label}>Deal Type</div>
                <div className="text-sm text-neutral mt-1">
                  {deal.dealType ? DEAL_TYPE_LABELS[deal.dealType] : '—'}
                </div>
              </div>
              <div className="px-4 py-4 border-b border-primary-subtle">
                <div className={label}>Priority</div>
                <div className="mt-1.5">
                  {priority ? (
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[13px] font-medium ${PRIORITY_PILL[priority]}`}
                    >
                      <Flag className="w-3.5 h-3.5" />
                      {DEAL_PRIORITY_LABELS[priority]}
                    </span>
                  ) : (
                    <span className="text-sm text-neutral-medium">—</span>
                  )}
                </div>
              </div>
            </div>

            {/* Deal owner — PATCH on select, Admin/Manager only */}
            <InlineOwnerField
              label="Deal Owner"
              owners={owners}
              value={deal.owner?.uuid ?? null}
              editable={canReassignOwner}
              placeholder={ownersLoading ? 'Loading owners...' : 'Unassigned'}
              onSave={(ownerUuid: any) => patchDeal({ dealOwnerUuid: ownerUuid ?? undefined })}
              errorMessage="Could not reassign this deal. Please try again."
            />
            {/* Description */}
            <div>
              <div className="text-[11px] font-medium tracking-wide uppercase text-neutral-medium pt-2 pb-2">
                Insights
              </div>
              <div className="py-2 px-3 rounded-lg bg-primary-surface border border-primary-subtle max-h-32 overflow-y-auto">
                <p className="text-sm text-neutral-faded whitespace-pre-wrap [overflow-wrap:anywhere]">
                  {deal.description || '—'}
                </p>
              </div>
            </div>
          </>
        )}

        <DeleteModal
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={handleConfirmDelete}
          isDeleting={deleteDeal.isPending}
          entityLabel="Deal"
          entityName={deal.name}
          description={
            <>
              <strong className="text-white font-semibold">{deal.name}</strong> will be permanently
              deleted from your CRM.
            </>
          }
          cascade={deleteCascade}
          preserved="Contacts and Companies linked to this deal will remain in your CRM — only their association with this deal is removed."
        />
      </div>
    </FormikProvider>
  );
};

export default DealDetailsCard;